/* gk-engine.js — Granitkappen-Konfigurator
 *
 * Zustand, Rendering, Ziehen, Variantenaufloesung, Warenkorb.
 *
 * Grundsaetze:
 *  - EIN Objekt `entwurf`. Jede Eingabe geht durch setState(), danach wird neu
 *    gerendert. Der DOM ist nie Wahrheitsquelle.
 *  - Nichts ist waehlbar, was nicht bestellbar ist.
 *  - JEDE ZEILE ist einzeln beweglich. Es gibt keinen gemeinsamen Textblock.
 *  - Kein Monkey-Patching von fetch oder XMLHttpRequest.
 *  - Praefix gk- / GK_ ueberall, damit der Findling-Konfigurator ungestoert bleibt.
 */

import { preisklasse, gravurstufe, gravurflaeche, zeilenKapazitaet,
         aufschlagAusVarianten, alleGravurstufen, gevierGroesse,
         farbStatistik, farbeErlaubt, MAX_STANDARDFARBEN } from './gk-preis.js?v=20261002a';
import { entwurfZuSvg, svgAlsText, entwurfAlsDaten, datenZuCode } from './gk-svg.js?v=20261005a';

const EUR = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });

/* Buchstabenabstand in Tausendstel Geviert, wie die Laufweite in Illustrator.
 * Weiter darf mehr als enger (Festlegung Sonja, 05.10.2026). */
const ABSTAND_MIN = -50;
const ABSTAND_MAX = 200;
function abstandZahl(a) {
  const n = Math.round(Number(a) || 0);
  return (n > 0 ? '+' : n < 0 ? '−' : '') + Math.abs(n);
}
function abstandText(a) {
  const n = Number(a) || 0;
  if (n === 0) return 'normal';
  if (n < 0) return 'enger';
  if (n <= 50) return 'etwas weiter';
  if (n <= 120) return 'weiter';
  return 'sehr weit';
}
const ZAHL = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 });

export class GkKonfigurator {
  constructor(wurzel, stamm) {
    this.wurzel = wurzel;
    this.stamm = stamm;
    this.abschnittId = wurzel.dataset.abschnitt;
    this.galerieOffen = false;
    this.service = { an: false, text: '' };

    this.varianten = stamm.varianten || [];
    this.hatVarianten = this.varianten.length > 0;
    this.vorschauModus = !!stamm.einstellungen.vorschau_ohne_varianten;

    if (!this.pruefeStammdaten()) return;

    this.entwurf = this.startEntwurf();
    this.verdrahte();
    this.alleNeu();
    this.ladeMotivSvgs();

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        this.pruefeSchriften();
        this.zeichneBuehne();
      });
    }
  }

  pruefeStammdaten() {
    const fehlt = [];
    if (!this.stamm.steine.length) fehlt.push('keine Steine mit Status verfuegbar');
    if (!this.stamm.klassen.length) fehlt.push('keine Preisklassen angelegt');
    if (!this.stamm.schriften.length) fehlt.push('keine aktiven Schriften');
    if (!this.stamm.gravurfarben.length) fehlt.push('keine aktiven Gravurfarben');

    const ohneFaktor = this.stamm.schriften.filter(s => !(Number(s.versalhoehe_faktor) > 0));
    if (ohneFaktor.length) {
      this.stamm.schriften = this.stamm.schriften.filter(s => Number(s.versalhoehe_faktor) > 0);
      console.warn('[GK] Ohne Versalhoehe-Faktor, nicht angeboten:',
        ohneFaktor.map(s => s.anzeigename).join(', '));
      if (!this.stamm.schriften.length) fehlt.push('keine Schrift mit Versalhoehe-Faktor');
    }
    this.stamm.schriften.sort((a, b) => Number(a.sortierung || 99) - Number(b.sortierung || 99));
    this.stamm.gravurfarben.sort((a, b) => Number(a.sortierung || 99) - Number(b.sortierung || 99));

    const ohneKlasse = [];
    this.stamm.steine = this.stamm.steine.filter(s => {
      const k = preisklasse(s, this.stamm.klassen);
      if (!k) { ohneKlasse.push(s.bezeichnung); return false; }
      s._klasse = k;
      return true;
    });
    if (ohneKlasse.length) {
      console.warn('[GK] Ohne passende Preisklasse, nicht angeboten:', ohneKlasse.join(', '));
    }
    if (!this.stamm.steine.length) fehlt.push('kein Stein faellt in eine Preisklasse');
    this.stamm.steine.sort((a, b) => Number(a.breite_cm) - Number(b.breite_cm));

    if (this.hatVarianten) {
      const noetig = alleGravurstufen(this.stamm.einstellungen.max_zeilen);
      const luecken = [];
      for (const k of this.stamm.klassen) {
        for (const g of noetig) {
          const da = this.varianten.some(v => v.steinklasse === k.variantenwert && v.gravur === g);
          if (!da) luecken.push(k.variantenwert + ' / ' + g);
        }
      }
      if (luecken.length) fehlt.push('Varianten fehlen: ' + luecken.join(', '));
    } else if (!this.vorschauModus) {
      fehlt.push('Optionsachse Steinklasse fehlt am Produkt');
    }

    if (fehlt.length) {
      console.error('[GK] Konfigurator rendert nicht:', fehlt.join(' · '));
      this.wurzel.hidden = true;
      return false;
    }
    return true;
  }

  pruefeSchriften() {
    if (!document.fonts || !document.fonts.check) return;
    const fehlend = this.stamm.schriften.filter(
      s => !document.fonts.check('16px "' + s.css_family + '"')
    );
    if (fehlend.length) {
      console.warn('[GK] Nicht im Adobe-Kit geladen:', fehlend.map(s => s.css_family).join(', '));
    }
  }

  startEntwurf() {
    const stein = this.stamm.steine[0];
    const e = { stein, zeilen: [this.neueZeile()], motiv: null, _hilfslinie: null };
    if (this.stamm.festes_motiv) {
      e.motiv = {
        handle: this.stamm.festes_motiv.handle,
        farbe: this.stamm.gravurfarben[0].handle,
        breite_mm: this.motivStartbreite(this.stamm.festes_motiv, stein),
        x: 0, y: 0
      };
    } else if (this.stamm.start_motiv) {
      // Startmotiv je Produkt (z. B. goldenes Herz): vorausgewaehlt, aber frei aenderbar
      const mo = this.stamm.motive.find(x => x.handle === this.stamm.start_motiv);
      if (mo) {
        e.motiv = {
          handle: mo.handle,
          farbe: this.motivStartFarbe(e),
          breite_mm: this.motivStartbreite(mo, stein),
          x: 0, y: 0
        };
      }
    }
    this.stapleNeu(e);
    return e;
  }

  /* Farbe fuer ein neu gewaehltes Motiv: die Startfarbe des Produkts, wenn
   * gesetzt und vorhanden, sonst wie bei Zeilen. */
  motivStartFarbe(e) {
    const h = this.stamm.motiv_startfarbe;
    if (h && this.stamm.gravurfarben.some(c => c.handle === h)) return h;
    const z0 = e && e.zeilen && e.zeilen[0];
    return z0 && z0.farbe ? z0.farbe : this.stamm.gravurfarben[0].handle;
  }

  /* Motivbreite: zuerst die Spanne am Motiv (min_breite_mm / max_breite_mm),
   * sonst die Abschnitts-Einstellungen. Nach oben zusaetzlich durch die Breite
   * der Gravurflaeche begrenzt. */
  motivGrenzen(stein, motiv) {
    const e = this.stamm.einstellungen;
    const eigMin = Number(motiv && motiv.min_breite_mm);
    const eigMax = Number(motiv && motiv.max_breite_mm);
    const min = eigMin > 0 ? eigMin : (Number(e.motiv_min_mm) || 70);
    const oben = eigMax > 0 ? eigMax : (Number(e.motiv_max_mm) || 140);
    const f = gravurflaeche(stein || this.entwurf.stein, e.gravurVorgabe);
    const max = Math.max(min, Math.min(oben, Math.floor(f.w - 6)));
    return { min, max };
  }

  motivStartbreite(motiv, stein) {
    const g = this.motivGrenzen(stein, motiv);
    const std = Number(motiv && motiv.standardbreite_mm) || 0;
    return Math.max(g.min, Math.min(g.max, std || g.min));
  }

  neueZeile() {
    return {
      text: '',
      schrift: this.stamm.schriften[0].handle,
      farbe: this.startFarbe(),
      groesse_mm: Math.max(this.stamm.einstellungen.mindest_versalhoehe_mm, 12),
      abstand: 0,
      ausrichtung: 'mitte',
      x: null, y: null
    };
  }

  hoeheMm(zeile) {
    const s = this.stamm.schriften.find(x => x.handle === zeile.schrift);
    const g = gevierGroesse(zeile.groesse_mm, s);
    return g == null ? Number(zeile.groesse_mm) : g;
  }

  /* Setzt ALLE Positionen neu: Motiv oben mittig, Zeilen darunter gestapelt
   * und als Gruppe senkrecht zentriert. Das ist der Ausgangszustand und das,
   * was "Anordnung zuruecksetzen" wiederherstellt. */
  stapleNeu(e) {
    const f = gravurflaeche(e.stein, this.stamm.einstellungen.gravurVorgabe);
    const abstand = this.stamm.einstellungen.zeilenabstand;
    const mitte = f.x + f.w / 2;
    const mitMotiv = !!(e.motiv && e.motiv.handle);

    let oben = f.y;
    let hoehe = f.h;
    if (mitMotiv) {
      const mh = this.motivHoeheVon(e.motiv);
      e.motiv.x = mitte;
      e.motiv.y = f.y + mh / 2 + f.h * 0.06;
      const belegt = mh + f.h * 0.06 + this.stamm.einstellungen.motiv_abstand_mm;
      oben = f.y + belegt;
      hoehe = f.h - belegt;
    }

    const zeilen = e.zeilen;
    const hoehen = zeilen.map(z => this.hoeheMm(z) * abstand);
    const gesamt = hoehen.reduce((a, b) => a + b, 0);
    let cy = oben + Math.max(0, (hoehe - gesamt) / 2);
    zeilen.forEach((z, i) => {
      cy += hoehen[i];
      z.x = mitte;
      z.y = Math.min(f.y + f.h - 2, cy - hoehen[i] * (abstand - 1) / abstand);
    });
  }

  /* Eine neu erschienene Zeile bekommt einen Platz unter der letzten, ohne die
   * vorhandenen zu verschieben. Der Kunde soll seine Anordnung behalten. */
  platziereNeue(e) {
    const f = gravurflaeche(e.stein, this.stamm.einstellungen.gravurVorgabe);
    const abstand = this.stamm.einstellungen.zeilenabstand;
    const mitte = f.x + f.w / 2;
    let letzteY = null, letzteX = mitte;
    for (const z of e.zeilen) {
      if (z.y != null) { letzteY = z.y; letzteX = z.x; }
    }
    const unten = f.y + f.h - 2;
    for (const z of e.zeilen) {
      if (z.x != null && z.y != null) continue;
      const h = this.hoeheMm(z) * abstand;
      // Passt die neue Zeile nicht mehr unter die letzte, wird alles neu
      // verteilt. Sonst landet sie auf der vorigen Zeile.
      if (letzteY != null && letzteY + h > unten) { this.stapleNeu(e); return; }
      z.x = letzteX;
      z.y = letzteY == null ? f.y + f.h / 2 : letzteY + h;
      letzteY = z.y;
    }
  }

  setState(aendern) { aendern(this.entwurf); this.alleNeu(); }

  klasse() { return this.entwurf.stein._klasse; }
  stufe() { return gravurstufe(this.entwurf, this.stamm.gravurfarben); }

  variante() {
    if (!this.hatVarianten) return null;
    const s = this.stufe();
    if (!s) return null;
    return this.varianten.find(
      v => v.steinklasse === this.klasse().variantenwert && v.gravur === s
    ) || null;
  }

  motivDaten(handle) {
    const alle = this.stamm.motive.concat(this.stamm.festes_motiv ? [this.stamm.festes_motiv] : []);
    return alle.find(x => x && x.handle === handle) || null;
  }

  /* Hoehe des Motivs in mm: Breite x Seitenverhaeltnis der SVG (sonst quadratisch). */
  motivHoeheVon(motiv) {
    if (!motiv || !motiv.handle) return 0;
    const m = this.motivDaten(motiv.handle);
    const b = Number(motiv.breite_mm);
    return (m && m.svg_breite && m.svg_hoehe) ? b * Number(m.svg_hoehe) / Number(m.svg_breite) : b;
  }

  motivHoeheMm() { return this.motivHoeheVon(this.entwurf.motiv); }

  /* Laedt die Motiv-SVGs (Feld svg_datei bzw. ein SVG im Feld bild), entfernt
   * eigene Farben und merkt sich Pfade + viewBox. Danach kann jedes Motiv in der
   * Gravurfarbe gezeichnet werden. Schlaegt das Laden fehl, bleibt das Bild. */
  ladeMotivSvgs() {
    const alle = this.stamm.motive.concat(this.stamm.festes_motiv ? [this.stamm.festes_motiv] : []);
    const auftraege = alle.filter(Boolean).map(m => this.ladeMotivSvg(m));
    Promise.all(auftraege).then(() => {
      if (this.entwurf.motiv && this.entwurf.motiv.handle) this.stapleNeu(this.entwurf);
      this.alleNeu();
    });
  }

  async ladeMotivSvg(m) {
    const url = m.svg_url || (m.bild_url && /\.svg(\?|$)/i.test(m.bild_url) ? m.bild_url : null);
    if (!url || m.svg_pfade) return;
    try {
      const antwort = await fetch(url);
      if (!antwort.ok) throw new Error('HTTP ' + antwort.status);
      const doc = new DOMParser().parseFromString(await antwort.text(), 'image/svg+xml');
      const wurzel = doc.documentElement;
      if (!wurzel || wurzel.nodeName.toLowerCase() !== 'svg') throw new Error('keine SVG');
      let vb = (wurzel.getAttribute('viewBox') || '').trim().split(/[\s,]+/).map(Number);
      if (vb.length !== 4 || vb.some(n => !isFinite(n))) {
        vb = [0, 0, parseFloat(wurzel.getAttribute('width')) || 100,
              parseFloat(wurzel.getAttribute('height')) || 100];
      }
      wurzel.querySelectorAll('script, style, title, desc, metadata').forEach(n => n.remove());
      wurzel.querySelectorAll('*').forEach(el => {
        const stil = el.getAttribute('style') || '';
        const hatteStrich = (el.getAttribute('stroke') && el.getAttribute('stroke') !== 'none') ||
          /stroke\s*:\s*(?!none)/i.test(stil);
        const ohneFuellung = el.getAttribute('fill') === 'none' || /fill\s*:\s*none/i.test(stil);
        el.removeAttribute('fill');
        el.removeAttribute('stroke');
        el.removeAttribute('class');
        if (stil) {
          el.setAttribute('style', stil.replace(/(^|;)\s*(fill|stroke)\s*:[^;]*/gi, '$1'));
        }
        if (ohneFuellung) el.setAttribute('fill', 'none');
        if (hatteStrich && !el.getAttribute('stroke-width')) el.setAttribute('stroke-width', '1');
      });
      const innen = Array.from(wurzel.childNodes)
        .map(n => n.nodeType === 1 ? new XMLSerializer().serializeToString(n) : '')
        .join('')
        .replace(/ xmlns="http:\/\/www\.w3\.org\/2000\/svg"/g, '');
      m.svg_pfade = (vb[0] || vb[1])
        ? '<g transform="translate(' + (-vb[0]) + ' ' + (-vb[1]) + ')">' + innen + '</g>'
        : innen;
      m.svg_breite = vb[2];
      m.svg_hoehe = vb[3];
    } catch (fehler) {
      console.warn('[GK] Motiv-SVG nicht geladen, zeige Bild:', m.motivnummer, fehler);
    }
  }

  maxZeilen() {
    const e = this.stamm.einstellungen;
    const kleinster = Math.min(...this.stamm.schriften.map(s => Number(s.versalhoehe_faktor)));
    return zeilenKapazitaet(this.entwurf.stein, {
      gravurVorgabe: e.gravurVorgabe,
      mindestVersalhoeheMm: e.mindest_versalhoehe_mm,
      kleinsterVersalFaktor: kleinster,
      zeilenabstand: e.zeilenabstand,
      motivHoeheMm: this.motivHoeheMm(),
      motivAbstandMm: e.motiv_abstand_mm,
      maxZeilen: this.zeilenObergrenze()
    });
  }

  /* Obergrenze aus dem Produkt (max_zeilen); Steine ab Klasse 2 bekommen
   * mindestens 4 Zeilen. Der Platz auf dem Stein begrenzt zusaetzlich. */
  zeilenObergrenze() {
    const basis = Number(this.stamm.einstellungen.max_zeilen) || 3;
    const k = this.entwurf && this.entwurf.stein && this.entwurf.stein._klasse;
    const nr = k ? parseInt(String(k.variantenwert).replace(/\D+/g, ''), 10) : 0;
    return nr >= 2 ? Math.max(basis, 4) : basis;
  }

  /* Nur Vorschau und Summe, ohne die Eingabefelder anzufassen. */
  leichtNeu() {
    this.zeichneBuehne();
    this.zeichneSumme();
  }

  gefuellteZeilen() { return this.entwurf.zeilen.filter(z => z.text.trim() !== ''); }

  alleNeu() {
    this.beschneideZeilen();
    this.platziereNeue(this.entwurf);
    this.zeichneBuehne();
    this.zeichneStein();
    this.zeichneZeilen();
    this.zeichneMotiv();
    this.zeichneSumme();
  }

  beschneideZeilen() {
    const platz = this.maxZeilen();
    const gefuellt = this.gefuellteZeilen().length;
    this.platzKonflikt = gefuellt > platz;
    const soll = Math.min(platz, Math.max(1, gefuellt + 1));
    while (this.entwurf.zeilen.length < soll) this.entwurf.zeilen.push(this.neueZeile());
    while (this.entwurf.zeilen.length > Math.max(soll, gefuellt)) this.entwurf.zeilen.pop();
  }

  zeichneBuehne() {
    const ziel = this.el('buehne');
    const s = this.entwurf.stein;
    const f = gravurflaeche(s, this.stamm.einstellungen.gravurVorgabe);
    ziel.style.aspectRatio = s.breite_cm + ' / ' + s.hoehe_cm;
    ziel.innerHTML = '';
    const bild = document.createElement('img');
    bild.className = 'gk-steinfoto';
    bild.src = s.foto_url;
    bild.alt = 'Granitkappe ' + s.bezeichnung + ', ' + s.farbton + ', ' + s.struktur;
    ziel.appendChild(bild);
    const svg = entwurfZuSvg(this.entwurf, this.stamm);
    ziel.appendChild(svg);
    this.el('buehne-stein').textContent =
      s.bezeichnung + ' · ' + ZAHL.format(s.breite_cm) + ' × ' + ZAHL.format(s.hoehe_cm) + ' cm';

    svg.querySelectorAll('[data-gk-zeilennr]').forEach(el => {
      const i = Number(el.dataset.gkZeilennr);
      this.ziehbar(el, this.entwurf.zeilen[i], f, 'Zeile ' + (i + 1));
    });
    const m = svg.querySelector('#gk-motiv');
    if (m && this.entwurf.motiv) this.ziehbar(m, this.entwurf.motiv, f, 'Motiv');
  }

  /* Fangpunkte fuer die Waagerechte: die Mitte der Gravurflaeche und die
   * x-Position der anderen Zeilen. So liegen Zeilen untereinander buendig,
   * ohne dass der Kunde zielen muss. */
  fangpunkte(ausser, f) {
    const p = [f.x + f.w / 2];
    for (const z of this.entwurf.zeilen) {
      if (z === ausser || z.x == null || z.text.trim() === '') continue;
      p.push(z.x);
    }
    if (this.entwurf.motiv && this.entwurf.motiv !== ausser && this.entwurf.motiv.handle) {
      p.push(this.entwurf.motiv.x);
    }
    return p;
  }

  ziehbar(el, ziel, f, name) {
    if (!el || !ziel) return;
    el.classList.add('gk-greif');
    el.setAttribute('tabindex', '0');
    el.setAttribute('role', 'button');
    el.setAttribute('aria-label', name + ' verschieben, mit den Pfeiltasten in Millimeterschritten');

    // Auf Touch-Geraeten: Nur Text und Motiv selbst halten den Finger fest.
    // Wischen auf dem uebrigen Stein scrollt die Seite ganz normal.
    el.addEventListener('touchstart', ev => ev.preventDefault(), { passive: false });

    el.addEventListener('pointerdown', ev => {
      ev.preventDefault();
      ev.stopPropagation();
      const svg = el.ownerSVGElement;
      const kasten = svg.getBoundingClientRect();
      const skalaX = f.bm / kasten.width;
      const skalaY = f.hm / kasten.height;
      const startX = ev.clientX, startY = ev.clientY;
      const a = { x: ziel.x, y: ziel.y };
      const punkte = this.fangpunkte(ziel, f);
      const toleranz = f.w * 0.02;
      const rand = 3;
      const bewegen = e => {
        let nx = a.x + (e.clientX - startX) * skalaX;
        let ny = a.y + (e.clientY - startY) * skalaY;
        let gefangen = null;
        for (const p of punkte) {
          if (Math.abs(nx - p) < toleranz) { nx = p; gefangen = p; break; }
        }
        ziel.x = Math.max(f.x + rand, Math.min(f.x + f.w - rand, nx));
        ziel.y = Math.max(f.y + rand, Math.min(f.y + f.h - rand, ny));
        this.entwurf._hilfslinie = gefangen;
        this.zeichneBuehne();
      };
      const los = () => {
        window.removeEventListener('pointermove', bewegen);
        window.removeEventListener('pointerup', los);
        window.removeEventListener('pointercancel', los);
        this.entwurf._hilfslinie = null;
        this.zeichneBuehne();
        this.schreibeVersteckteFelder(this.variante());
      };
      window.addEventListener('pointermove', bewegen);
      window.addEventListener('pointerup', los);
      window.addEventListener('pointercancel', los);
    });

    el.addEventListener('keydown', ev => {
      const schritt = ev.shiftKey ? 5 : 1;
      const m = { ArrowLeft: [-schritt, 0], ArrowRight: [schritt, 0],
                  ArrowUp: [0, -schritt], ArrowDown: [0, schritt] }[ev.key];
      if (!m) return;
      ev.preventDefault();
      ziel.x = Math.max(f.x + 3, Math.min(f.x + f.w - 3, ziel.x + m[0]));
      ziel.y = Math.max(f.y + 3, Math.min(f.y + f.h - 3, ziel.y + m[1]));
      this.zeichneBuehne();
      const neu = this.el('buehne').querySelector('#' + el.id);
      if (neu) neu.focus();
      this.schreibeVersteckteFelder(this.variante());
    });
  }

  zeichneStein() {
    const s = this.entwurf.stein;
    const k = s._klasse;
    this.el('stein').innerHTML =
      '<div class="gk-steinkarte">' +
      '<dl class="gk-steininfo">' +
      '<div><dt>Stein</dt><dd>' + s.bezeichnung + '</dd></div>' +
      '<div><dt>Farbe</dt><dd>' + s.farbton + ', ' + s.struktur + '</dd></div>' +
      '<div><dt>Größe</dt><dd>' + ZAHL.format(s.breite_cm) + ' × ' + ZAHL.format(s.hoehe_cm) +
        ' × ' + ZAHL.format(s.tiefe_cm) + ' cm</dd></div>' +
      '<div><dt>Gewicht</dt><dd>' + ZAHL.format(s.gewicht_kg) + ' kg</dd></div>' +
      '<div><dt>Preisklasse</dt><dd>' + k.variantenwert + ' · ' + this.abPreis(s) +
        ' <button type="button" class="gk-info" data-gk="preisinfo-oeffnen"' +
        ' aria-label="Wie sich der Preis zusammensetzt" title="Wie sich der Preis zusammensetzt">i</button>' +
        '</dd></div>' +
      '</dl>' +
      '<div class="gk-steinkarte-knopf">' +
      '<button type="button" class="gk-knopf gk-flach" data-gk="galerie">Anderen wählen</button>' +
      '</div></div>';
  }

  zeichneZeilen() {
    const beh = this.el('zeilen');
    const platz = this.maxZeilen();
    beh.innerHTML = '';
    this.entwurf.zeilen.forEach((z, i) => {
      const d = document.createElement('div');
      d.className = 'gk-zeile';
      d.innerHTML =
        '<div class="gk-zeile-kopf">' +
        '<label for="gk-z' + i + '">Zeile ' + (i + 1) + (i === 0 ? ' · Pflicht' : '') + '</label>' +
        '<span class="gk-zaehler">' + z.text.length + '/' +
        this.stamm.einstellungen.max_zeichen + '</span></div>' +
        '<input type="text" id="gk-z' + i + '" maxlength="' +
        this.stamm.einstellungen.max_zeichen + '" data-gk-zeile="' + i + '">' +
        '<div class="gk-zeile-raster">' +
        '<div class="gk-feld">' +
        '<label class="gk-feldname" for="gk-f' + i + '">Schrift</label>' +
        '<select id="gk-f' + i + '" data-gk-schrift="' + i + '">' +
        this.stamm.schriften.map(s =>
          '<option value="' + s.handle + '"' + (s.handle === z.schrift ? ' selected' : '') +
          '>' + s.anzeigename + '</option>').join('') +
        '</select></div>' +
        '<div class="gk-feld">' +
        '<label class="gk-feldname" for="gk-s' + i + '">Größe</label>' +
        '<div class="gk-regler">' +
        '<input type="range" id="gk-s' + i + '" data-gk-groesse="' + i + '"' +
        ' min="' + this.stamm.einstellungen.mindest_versalhoehe_mm + '" max="40" step="0.5"' +
        ' value="' + z.groesse_mm + '">' +
        '<span class="gk-zaehler">' + ZAHL.format(z.groesse_mm) + ' mm</span>' +
        '</div></div>' +
        '<div class="gk-feld">' +
        '<label class="gk-feldname" for="gk-a' + i + '">Buchstabenabstand</label>' +
        '<div class="gk-regler">' +
        '<input type="range" id="gk-a' + i + '" data-gk-abstand="' + i + '"' +
        ' min="' + ABSTAND_MIN + '" max="' + ABSTAND_MAX + '" step="10"' +
        ' value="' + (Number(z.abstand) || 0) + '">' +
        '<span class="gk-zaehler gk-abstandtext">' + abstandText(z.abstand) + '</span>' +
        '</div></div></div>' +
        '<div class="gk-feld">' +
        '<span class="gk-feldname">Gravurfarbe</span>' +
        '<div class="gk-farbpunkte" data-gk-farben="' + i + '"></div>' +
        '</div>';
      d.querySelector('input[type=text]').value = z.text;
      beh.appendChild(d);
      this.farbpunkte(d.querySelector('[data-gk-farben]'), z.farbe, handle => {
        this.setState(e => { e.zeilen[i].farbe = handle; });
      }, 'Farbe Zeile ' + (i + 1), 'z' + i);
    });

    if (this.entwurf.zeilen.length >= platz) {
      const p = document.createElement('p');
      p.className = 'gk-mehr';
      p.innerHTML = 'Mehr Zeilen passen auf diesen Stein nicht. ' +
        '<a href="' + this.stamm.einstellungen.kontakt_url + '">Schreib uns</a>, ' +
        'wir finden gemeinsam eine Lösung.';
      beh.appendChild(p);
    }
    if (this.platzKonflikt) {
      const p = document.createElement('p');
      p.className = 'gk-konflikt';
      p.textContent = 'Motiv und Text zusammen passen nicht mehr auf diesen Stein. ' +
        'Mach das Motiv kleiner oder nimm eine Zeile heraus.';
      beh.appendChild(p);
    }
  }

  farbpunkte(beh, gewaehlt, beiWahl, label, element) {
    beh.innerHTML = '';
    let gesperrt = 0;
    this.stamm.gravurfarben.forEach(f => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'gk-farbpunkt';
      b.style.background = f.hex_vorschau;
      const erlaubt = !element || f.handle === gewaehlt ||
        farbeErlaubt(this.entwurf, this.stamm.gravurfarben, element, f.handle);
      b.title = erlaubt ? f.label
        : f.label + ' – höchstens ' + MAX_STANDARDFARBEN + ' Gravurfarben plus Gold';
      b.setAttribute('aria-label', label + ': ' + f.label + (erlaubt ? '' : ' (nicht wählbar)'));
      b.setAttribute('aria-pressed', String(f.handle === gewaehlt));
      if (!erlaubt) {
        b.disabled = true; b.classList.add('gk-gesperrt');
        b.style.opacity = '0.3'; b.style.cursor = 'not-allowed';
        gesperrt++;
      }
      b.addEventListener('click', () => { if (erlaubt) beiWahl(f.handle); });
      beh.appendChild(b);
    });
    if (gesperrt) {
      const h = document.createElement('span');
      h.className = 'gk-zaehler gk-farbgrenze';
      h.textContent = 'Höchstens ' + MAX_STANDARDFARBEN + ' Farben plus Gold';
      beh.appendChild(h);
    }
  }

  /* Startfarbe für ein neues Element: die Farbe von Zeile 1 (gleiche Farbe =
   * kein Aufpreis), sonst die erste Gravurfarbe. Hält die Farbgrenze ein. */
  startFarbe() {
    const z0 = this.entwurf && this.entwurf.zeilen && this.entwurf.zeilen[0];
    return z0 && z0.farbe ? z0.farbe : this.stamm.gravurfarben[0].handle;
  }

  zeichneMotiv() {
    const beh = this.el('motiv');
    if (!beh) return;
    const fest = this.stamm.festes_motiv;
    const liste = this.stamm.motive;
    if (fest) {
      beh.innerHTML = '<p class="gk-erklaer">Dieses Motiv gehört zum Stein und bleibt.</p>' +
        '<div class="gk-motiv-fest"><img src="' + fest.bild_url + '" alt="' +
        fest.motivname + '" width="56"><span>' + fest.motivnummer + ' · ' +
        fest.motivname + '</span></div>';
    } else if (!liste.length) {
      beh.innerHTML = '<p class="gk-erklaer">Für dieses Angebot sind keine Motive hinterlegt.</p>';
    } else {
      beh.innerHTML = '<div class="gk-motive">' +
        '<button type="button" class="gk-motiv" data-gk-motiv=""' +
        ' aria-pressed="' + String(!this.entwurf.motiv) + '">' +
        '<span class="gk-motiv-kein">ohne</span><span>kein Motiv</span></button>' +
        liste.map(m =>
          '<button type="button" class="gk-motiv" data-gk-motiv="' + m.handle + '"' +
          ' aria-pressed="' + String(!!this.entwurf.motiv && this.entwurf.motiv.handle === m.handle) + '">' +
          '<img src="' + m.bild_url + '" alt="" loading="lazy">' +
          '<span>' + m.motivname + '</span></button>').join('') +
        '</div>';
    }
    const opt = this.el('motiv-optionen');
    if (!opt) return;
    opt.hidden = !(this.entwurf.motiv && this.entwurf.motiv.handle);
    if (opt.hidden) return;
    const regler = opt.querySelector('[data-gk-motivgroesse]');
    const g = this.motivGrenzen(null, this.motivDaten(this.entwurf.motiv.handle));
    regler.min = g.min; regler.max = g.max;
    regler.value = this.entwurf.motiv.breite_mm;
    opt.querySelector('[data-gk-motivmm]').textContent =
      ZAHL.format(this.entwurf.motiv.breite_mm) + ' mm';
    this.farbpunkte(opt.querySelector('[data-gk-motivfarben]'),
      this.entwurf.motiv.farbe,
      handle => this.setState(e => { e.motiv.farbe = handle; }), 'Motivfarbe', 'motiv');
  }

  zeichneSumme() {
    const s = this.entwurf.stein;
    const k = this.klasse();
    const stufe = this.stufe();
    const v = this.variante();
    const posten = [];
    let gesamt = null;

    if (v) {
      const basis = this.varianten.find(
        x => x.steinklasse === k.variantenwert && x.gravur === '1 Farbe');
      posten.push(['Stein ' + s.bezeichnung + ' (' + ZAHL.format(s.breite_cm) + ' × ' +
                   ZAHL.format(s.hoehe_cm) + ' cm)', Number(basis.preis)]);
      const auf = aufschlagAusVarianten(this.varianten, k.variantenwert, stufe);
      if (auf) posten.push(['Gravur: ' + stufe, auf]);
      gesamt = Number(v.preis);
    } else {
      posten.push(['Stein ' + s.bezeichnung + ' (' + k.variantenwert + ')', null]);
      posten.push(['Gravur: ' + (stufe || '—'), null]);
    }

    const sv = this.stamm.einstellungen.service;
    if (sv && this.service && this.service.an) {
      posten.push(['Persönlicher Gestaltungs-Service', Number(sv.preis)]);
      if (gesamt != null) gesamt += Number(sv.preis);
    }

    this.el('aufstellung').innerHTML = posten.map(p =>
      '<dt>' + p[0] + '</dt><dd>' + (p[1] == null ? '—' : EUR.format(p[1])) + '</dd>'
    ).join('') +
      '<dt class="gk-gesamt">Gesamt</dt><dd class="gk-gesamt">' +
      (gesamt == null ? 'Vorschau ohne Preis' : EUR.format(gesamt)) + '</dd>';

    const t = this.el('technik');
    if (t) {
      t.textContent = 'Variante: ' + k.variantenwert + ' / ' + (stufe || '—') +
        (v ? '   ·   SKU ' + v.sku : '') +
        '   ·   Stein wiegt ' + ZAHL.format(s.gewicht_kg) + ' kg';
    }

    const fehler = [];
    if (!this.entwurf.zeilen[0] || this.entwurf.zeilen[0].text.trim() === '') {
      fehler.push('Zeile 1 wird für die Gravur gebraucht.');
    }
    if (this.platzKonflikt) fehler.push('Der Entwurf passt so nicht auf den Stein.');
    const fst = farbStatistik(this.entwurf, this.stamm.gravurfarben);
    const zuVieleFarben = fst && fst.standard.size > MAX_STANDARDFARBEN;
    if (zuVieleFarben) {
      fehler.push('Bitte höchstens ' + MAX_STANDARDFARBEN + ' Gravurfarben plus Gold wählen.');
    } else if (!v && !this.vorschauModus) {
      fehler.push('Für diese Zusammenstellung fehlt eine Variante.');
    }

    const knopf = this.el('absenden');
    knopf.disabled = fehler.length > 0 || this.vorschauModus;
    if (this.vorschauModus) knopf.textContent = 'Vorschaumodus — noch nicht bestellbar';
    const f = this.el('fehler');
    f.hidden = fehler.length === 0;
    f.textContent = fehler.join(' ');

    this.schreibeVersteckteFelder(v);
    this.zeigeSvg();
  }

  zeigeSvg() {
    const pre = this.el('svg-quelltext');
    if (!pre) return;
    pre.textContent = svgAlsText(this.entwurf, this.stamm) || '(Stammdaten unvollstaendig)';
  }

  formular() {
    return document.querySelector('#product-form-' + this.abschnittId) ||
           this.wurzel.closest('form[action*="/cart/add"]') ||
           document.querySelector('form[action*="/cart/add"]');
  }

  /* Alle Bestell-Angaben als Line-Item-Properties. */
  felder() {
    const s = this.entwurf.stein;
    const code = datenZuCode(entwurfAlsDaten(this.entwurf, this.stamm));
    const felder = {
      'Ausgewählter Stein': s.bezeichnung + ' · ' + ZAHL.format(s.breite_cm) + ' × ' +
        ZAHL.format(s.hoehe_cm) + ' × ' + ZAHL.format(s.tiefe_cm) + ' cm',
      'Steinklasse': this.klasse().variantenwert,
      'Gravur': this.stufe() || ''
    };
    this.gefuellteZeilen().forEach((z, i) => {
      const schrift = this.stamm.schriften.find(x => x.handle === z.schrift);
      const farbe = this.stamm.gravurfarben.find(x => x.handle === z.farbe);
      felder['Zeile ' + (i + 1)] = z.text;
      felder['Schrift Zeile ' + (i + 1)] = schrift ? schrift.anzeigename : '';
      felder['Farbe Zeile ' + (i + 1)] = farbe ? farbe.label : '';
      felder['Höhe Zeile ' + (i + 1)] = ZAHL.format(z.groesse_mm) + ' mm';
      if (Number(z.abstand)) felder['Abstand Zeile ' + (i + 1)] = abstandZahl(z.abstand);
    });
    if (this.service && this.service.an) {
      felder['Gestaltungs-Service'] = 'Ja, persönlicher Entwurf';
      const w = (this.service.text || '').trim();
      if (w) felder['Sonderwünsche'] = w;
    }
    if (this.entwurf.motiv && this.entwurf.motiv.handle) {
      const m = this.motivDaten(this.entwurf.motiv.handle);
      const mf = this.stamm.gravurfarben.find(x => x.handle === this.entwurf.motiv.farbe);
      felder['Motiv'] = m ? m.motivnummer + ' · ' + m.motivname : '';
      felder['Farbe Motiv'] = mf ? mf.label : '';
      felder['Größe Motiv'] = ZAHL.format(this.entwurf.motiv.breite_mm) + ' mm';
    }
    felder['_Stein-ID'] = s.id;
    felder['_Entwurf'] = code;
    felder['_EntwurfLink'] = window.location.origin + this.stamm.einstellungen.entwurf_url + '?d=' + code;
    return felder;
  }

  schreibeVersteckteFelder(v) {
    const form = this.formular();
    if (!form) return;
    if (v) {
      const id = form.querySelector('[name="id"]');
      if (id) id.value = v.id;
    }
    const felder = this.felder();
    form.querySelectorAll('[data-gk-feld]').forEach(el => el.remove());
    Object.keys(felder).forEach(k => {
      const i = document.createElement('input');
      i.type = 'hidden';
      i.name = 'properties[' + k + ']';
      i.value = felder[k];
      i.dataset.gkFeld = '1';
      form.appendChild(i);
    });
  }

  /* Stein plus, wenn gewaehlt, der Gestaltungs-Service als eigene Position. */
  warenkorbPosten(v) {
    const posten = [{ id: v.id, quantity: 1, properties: this.felder() }];
    const sv = this.stamm.einstellungen.service;
    if (sv && this.service && this.service.an) {
      posten.push({
        id: sv.variante_id, quantity: 1,
        properties: {
          'Für Stein': this.entwurf.stein.bezeichnung + ' · ' + (this.stamm.produkt.titel || ''),
          'Wünsche': (this.service.text || '').trim() || 'siehe E-Mail'
        }
      });
    }
    return posten;
  }

  /* Legt die passende Variante mit allen Angaben direkt in den Warenkorb.
   * Unabhaengig vom Kaufen-Block des Themes (der darf ausgeblendet sein). */
  async inDenWarenkorb() {
    const v = this.variante();
    const knopf = this.el('absenden');
    const f = this.el('fehler');
    if (!v) return;
    knopf.disabled = true;
    const text = knopf.textContent;
    knopf.textContent = 'Wird hinzugefügt …';
    try {
      const antwort = await fetch((window.routes && window.routes.cart_add_url || '/cart/add') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: this.warenkorbPosten(v) })
      });
      if (!antwort.ok) {
        let meldung = 'HTTP ' + antwort.status;
        try { const j = await antwort.json(); meldung = j.description || j.message || meldung; } catch (x) {}
        throw new Error(meldung);
      }
      window.location.href = (window.routes && window.routes.cart_url) || '/cart';
    } catch (fehler) {
      console.error('[GK] Warenkorb:', fehler);
      f.hidden = false;
      f.textContent = 'Das hat nicht geklappt: ' + fehler.message + '. Bitte lade die Seite neu oder schreib uns.';
      knopf.disabled = false;
      knopf.textContent = text;
    }
  }

  async oeffneGalerie() {
    if (this.galerieOffen) return;
    this.galerieOffen = true;
    const schicht = document.createElement('div');
    schicht.className = 'gk-galerie-schicht';
    schicht.innerHTML = '<div class="gk-galerie" role="dialog" aria-modal="true"' +
      ' aria-label="Stein aussuchen"><p class="gk-laedt">Steine werden geladen …</p></div>';
    document.body.appendChild(schicht);
    this.schicht = schicht;

    const zu = () => this.schliesseGalerie();
    schicht.addEventListener('click', e => { if (e.target === schicht) zu(); });
    this._esc = e => { if (e.key === 'Escape') zu(); };
    document.addEventListener('keydown', this._esc);

    try {
      const url = '/?section_id=gk-steingalerie&product=' + this.stamm.produkt.handle;
      const antwort = await fetch(url);
      if (!antwort.ok) throw new Error('HTTP ' + antwort.status);
      const html = await antwort.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const inhalt = doc.querySelector('[data-gk-galerie]');
      if (!inhalt) throw new Error('Abschnitt gk-steingalerie nicht gefunden');
      schicht.querySelector('.gk-galerie').innerHTML = inhalt.innerHTML;
      this.verdrahteGalerie(schicht);
    } catch (fehler) {
      console.error('[GK] Galerie konnte nicht geladen werden:', fehler);
      schicht.querySelector('.gk-galerie').innerHTML =
        '<p class="gk-laedt">Die Steinauswahl lässt sich gerade nicht öffnen. ' +
        'Lade die Seite neu oder schreib uns.</p>' +
        '<p style="text-align:center;padding-bottom:20px">' +
        '<button type="button" class="gk-knopf" data-gk-zu>Schließen</button></p>';
      schicht.querySelector('[data-gk-zu]').addEventListener('click', zu);
    }
  }

  schliesseGalerie() {
    if (this.schicht) this.schicht.remove();
    this.schicht = null;
    this.galerieOffen = false;
    document.removeEventListener('keydown', this._esc);
  }

  verdrahteGalerie(schicht) {
    schicht.querySelectorAll('[data-gk-zu]').forEach(
      b => b.addEventListener('click', () => this.schliesseGalerie()));
    const filter = { laenge: null, farbton: new Set(), struktur: new Set(), klasse: new Set() };

    const passt = (s, ohne) => {
      if (ohne !== 'farbton' && filter.farbton.size && !filter.farbton.has(s.farbton)) return false;
      if (ohne !== 'struktur' && filter.struktur.size && !filter.struktur.has(s.struktur)) return false;
      if (ohne !== 'klasse' && filter.klasse.size && !filter.klasse.has(s._klasse.variantenwert)) return false;
      if (ohne !== 'laenge' && filter.laenge != null && Number(s.breite_cm) > filter.laenge) return false;
      return true;
    };

    const zeichne = () => {
      schicht.querySelectorAll('[data-gk-chip]').forEach(c => {
        const art = c.dataset.gkChip, wert = c.dataset.gkWert;
        let an, n;
        if (art === 'laenge') {
          const l = wert === '' ? null : Number(wert);
          an = filter.laenge === l;
          n = this.stamm.steine.filter(
            s => passt(s, 'laenge') && (l == null || Number(s.breite_cm) <= l)).length;
        } else {
          an = filter[art].has(wert);
          n = this.stamm.steine.filter(s => {
            if (!passt(s, art)) return false;
            if (art === 'klasse') return s._klasse.variantenwert === wert;
            return s[art] === wert;
          }).length;
        }
        c.setAttribute('aria-pressed', String(an));
        c.disabled = n === 0 && !an;
        const z = c.querySelector('.gk-n');
        if (z) z.textContent = n;
      });

      const treffer = this.stamm.steine.filter(s => passt(s, null));
      const zaehler = schicht.querySelector('[data-gk-treffer]');
      if (zaehler) {
        zaehler.textContent = treffer.length + ' von ' + this.stamm.steine.length + ' verfügbar';
      }
      const raster = schicht.querySelector('[data-gk-kacheln]');
      if (!treffer.length) {
        raster.innerHTML = '<p class="gk-leer">Zu dieser Auswahl haben wir gerade keinen Stein. ' +
          'Nimm einen Filter heraus.</p>';
        return;
      }
      raster.innerHTML = treffer.map(s =>
        '<button type="button" class="gk-kachel" data-gk-stein="' + s.handle + '"' +
        ' aria-pressed="' + String(s.handle === this.entwurf.stein.handle) + '">' +
        '<img src="' + s.foto_gross + '" alt="Granitkappe ' + s.bezeichnung + '" loading="lazy">' +
        '<span class="gk-k-nr">' + s.bezeichnung + '</span>' +
        '<span class="gk-k-masse">' + ZAHL.format(s.breite_cm) + ' × ' + ZAHL.format(s.hoehe_cm) +
        ' cm · ' + ZAHL.format(s.gewicht_kg) + ' kg</span>' +
        '<span class="gk-k-masse">' + s.farbton + ', ' + s.struktur + '</span>' +
        '<span class="gk-k-preis">' + this.abPreis(s) + '</span></button>').join('');

      raster.querySelectorAll('[data-gk-stein]').forEach(b =>
        b.addEventListener('click', () => {
          const neu = this.stamm.steine.find(x => x.handle === b.dataset.gkStein);
          this.schliesseGalerie();
          this.setState(e => {
            e.stein = neu;
            if (e.motiv && e.motiv.handle) {
              const g = this.motivGrenzen(neu, this.motivDaten(e.motiv.handle));
              e.motiv.breite_mm = Math.max(g.min, Math.min(g.max, Number(e.motiv.breite_mm)));
            }
            this.stapleNeu(e);
          });
        }));
    };

    schicht.querySelectorAll('[data-gk-chip]').forEach(c =>
      c.addEventListener('click', () => {
        const art = c.dataset.gkChip, wert = c.dataset.gkWert;
        if (art === 'laenge') filter.laenge = wert === '' ? null : Number(wert);
        else {
          const m = filter[art];
          if (m.has(wert)) m.delete(wert); else m.add(wert);
        }
        zeichne();
      }));
    zeichne();
  }

  abPreis(stein) {
    if (!this.hatVarianten) return stein._klasse.variantenwert;
    const v = this.varianten.find(
      x => x.steinklasse === stein._klasse.variantenwert && x.gravur === '1 Farbe');
    return v ? 'ab ' + EUR.format(Number(v.preis)) : stein._klasse.variantenwert;
  }

  el(name) { return this.wurzel.querySelector('[data-gk="' + name + '"]'); }

  verdrahte() {
    this.wurzel.addEventListener('click', ev => {
      if (ev.target.closest('[data-gk="galerie"]')) {
        ev.preventDefault(); this.oeffneGalerie(); return;
      }
      const m = ev.target.closest('[data-gk-motiv]');
      if (m) {
        ev.preventDefault();
        const handle = m.dataset.gkMotiv;
        this.setState(e => {
          if (!handle) { e.motiv = null; this.stapleNeu(e); return; }
          const mo = this.stamm.motive.find(x => x.handle === handle);
          e.motiv = {
            handle,
            farbe: e.motiv ? e.motiv.farbe : this.motivStartFarbe(e),
            breite_mm: this.motivStartbreite(mo, e.stein),
            x: 0, y: 0
          };
          this.stapleNeu(e);
        });
        return;
      }
      if (ev.target.closest('[data-gk="preisinfo-oeffnen"]')) {
        ev.preventDefault();
        const d = this.wurzel.querySelector('.gk-preisinfo');
        if (d) { d.open = !d.open; if (d.open) d.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); }
        return;
      }
      if (ev.target.closest('[data-gk="zuruecksetzen"]')) {
        ev.preventDefault();
        this.setState(e => this.stapleNeu(e));
      }
    });

    this.wurzel.addEventListener('input', ev => {
      const t = ev.target;
      if (t.dataset.gkZeile !== undefined) {
        const i = Number(t.dataset.gkZeile);
        const wert = t.value;
        const stelle = t.selectionStart;
        this.setState(e => {
          const warLeer = e.zeilen[i].text.trim() === '';
          e.zeilen[i].text = wert;
          // Zeile wird gerade aktiv: ihre Startfarbe darf die Farbgrenze nicht sprengen
          if (warLeer && wert.trim() !== '' &&
              !farbeErlaubt(e, this.stamm.gravurfarben, 'z' + i, e.zeilen[i].farbe)) {
            e.zeilen[i].farbe = this.startFarbe();
          }
        });
        const neu = this.wurzel.querySelector('[data-gk-zeile="' + i + '"]');
        if (neu) { neu.focus(); try { neu.setSelectionRange(stelle, stelle); } catch (x) {} }
        return;
      }
      /* Regler der Zeilen: waehrend des Ziehens nur Vorschau, Preis und Anzeige
       * neu zeichnen. Ein komplettes Neuzeichnen ersetzt den Regler unter dem
       * Finger, dann bricht das Ziehen ab. Alles andere folgt beim Loslassen. */
      if (t.dataset.gkGroesse !== undefined) {
        const i = Number(t.dataset.gkGroesse);
        this.entwurf.zeilen[i].groesse_mm = Number(t.value);
        const z = t.parentNode.querySelector('.gk-zaehler');
        if (z) z.textContent = ZAHL.format(Number(t.value)) + ' mm';
        this.leichtNeu();
        return;
      }
      if (t.dataset.gkWuensche !== undefined) {
        this.service.text = t.value;
        this.schreibeVersteckteFelder(this.variante());
        return;
      }
      if (t.dataset.gkAbstand !== undefined) {
        const i = Number(t.dataset.gkAbstand);
        this.entwurf.zeilen[i].abstand = Number(t.value);
        const z = t.parentNode.querySelector('.gk-zaehler');
        if (z) z.textContent = abstandText(Number(t.value));
        this.leichtNeu();
        return;
      }
      if (t.dataset.gkMotivgroesse !== undefined) {
        this.setState(e => { e.motiv.breite_mm = Number(t.value); });
      }
    });

    this.wurzel.addEventListener('change', ev => {
      const t = ev.target;
      if (t.dataset.gkSchrift !== undefined) {
        const i = Number(t.dataset.gkSchrift);
        this.setState(e => { e.zeilen[i].schrift = t.value; });
      }
      if (t.dataset.gkGroesse !== undefined || t.dataset.gkAbstand !== undefined) {
        this.setState(() => {});
      }
      if (t.dataset.gkService !== undefined) {
        this.service.an = t.checked;
        const w = this.el('service-wuensche');
        if (w) w.hidden = !t.checked;
        this.zeichneSumme();
      }
    });

    const form = this.formular();
    if (form) {
      form.addEventListener('submit', () => this.schreibeVersteckteFelder(this.variante()), true);
      form.querySelectorAll('[type="submit"], [name="add"]').forEach(b =>
        b.addEventListener('click', () => this.schreibeVersteckteFelder(this.variante()), true));
    }

    const absenden = this.el('absenden');
    if (absenden) {
      absenden.addEventListener('click', () => { this.inDenWarenkorb(); });
    }

    const svgKnopf = this.el('svg-kopieren');
    if (svgKnopf) {
      svgKnopf.addEventListener('click', async () => {
        const txt = svgAlsText(this.entwurf, this.stamm);
        if (!txt) return;
        try {
          await navigator.clipboard.writeText(txt);
          this.meldung('SVG-Code kopiert.');
        } catch (x) {
          this.meldung('Kopieren nicht erlaubt — markiere den Code von Hand.');
        }
      });
    }
  }

  meldung(text) {
    const alt = document.querySelector('.gk-toast');
    if (alt) alt.remove();
    const t = document.createElement('div');
    t.className = 'gk-toast';
    t.setAttribute('role', 'status');
    t.textContent = text;
    document.body.appendChild(t);
    setTimeout(() => t.remove(), 3200);
  }
}

function starte() {
  document.querySelectorAll('[data-gk-konfigurator]').forEach(el => {
    if (el.dataset.gkGestartet) return;
    el.dataset.gkGestartet = '1';
    const quelle = document.getElementById(el.dataset.gkDaten);
    if (!quelle) { console.error('[GK] Datenblock fehlt'); return; }
    let stamm;
    try { stamm = JSON.parse(quelle.textContent); }
    catch (x) { console.error('[GK] Datenblock unlesbar:', x); return; }
    new GkKonfigurator(el, stamm);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', starte);
} else {
  starte();
}
document.addEventListener('shopify:section:load', starte);